<template>
    <div class="grid" v-if="!$isLoading('metrics') && !hasServerError && !hasSegments">
        <div class="grid-item">
            <v-no-content>
                <span slot="title">No segments are visible or active</span>
                <span slot="description">Please enable or add at least one segment to view chart data.</span>
            </v-no-content>
        </div>
    </div>
    <draggable
            class="grid"
            v-else-if="!$isLoading('metrics') && !hasServerError && hasSegments"
            :list="metrics"
            v-bind="draggableOptions"
            @start="onStart"
            @end="onEnd"
            @update="saveTileOrder">
        <div
            class="grid-item grid-item-half"
            v-for="(metric, metricIndex) in metrics" :key="metricIndex">
            <div
                class="tile tile-dashboard-stat">
                <div class="tile-title align-items-center">
                    <h2>{{metric.humanName}}</h2>
                    <v-icon-button
                        class="no-flex inline-s tile-dashboard-hide"
                        tooltip="Hide this tile"
                        @click="hideTile(metricIndex)"
                        type="basic"
                        :fitted="true"
                        ref="tooltipElement">
                        <v-icon icon="eye-closed"/>
                    </v-icon-button>
                    <v-icon-button class="no-flex drag-handle cursor-move" type="basic" :fitted="true">
                        <v-icon icon="drag-move"/>
                    </v-icon-button>
                </div>
                <div>
                    <metrics-tile
                        v-for="(segment, segmentIndex) in activeSegments"
                        v-show="!isHiddenSegment(segment)"
                        :key="segmentIndex"
                        :index="segmentIndex"
                        :relative-time="relativeTime"
                        :inverse="!metric.increaseIsGood"
                        :metric="metric"
                        :segment="segment"/>
                </div>
            </div>
        </div>
    </draggable>
</template>

<script>
    import draggable from 'vuedraggable';
    import draggableDefaults from '@/vendor/draggable/defaults';
    import {vIconButton, vIcon} from '@/components/form';
    import vNoContent from '@/components/v-no-content';
    import segmentsMixins from './segments-mixins';
    import MetricsTile from './metrics-tile';
    import Segment from './segment';
    import DashboardBus from './dashboard-bus';
    import PreferenceTypes from '@/store/preference-types';

    /**
     * This component renders the metrics on the Dashboard.
     */
    export default {
        name: 'metrics',
        mixins: [segmentsMixins],
        components: {
            vIcon, vIconButton, MetricsTile, vNoContent, draggable,
        },
        props: {
            /**
             * An object that defines the date period from which to pull metrics.
             *
             * @type {object}
             */
            period: {
                type: Object,
                default: null,
            },
        },
        data() {
            return {
                metrics: [],
                segments: null,
                draggableOptions: {
                    ...draggableDefaults,
                    // force fallback when lots of elements present due to chrome bug
                    // https://github.com/SortableJS/Sortable/issues/1418
                    forceFallback: true,
                    chosenClass: 'active-metric-sort',
                    dragClass: 'active-metric-drag',
                },
            };
        },
        computed: {
            relativeTime() {
                if (this.period) {
                    return this.period.start.from(this.period.end);
                }
                return null;
            },
            hasSegments() {
                if (this.activeSegments !== null) {
                    const hiddenSegments = this.$store.getters['dashboard/hiddenSegments'];
                    return this.activeSegments
                        .filter(segment => !hiddenSegments.includes(segment.id))
                        .length;
                }
                return false;
            },
            tilePreferences() {
                return this.getUserPreference(PreferenceTypes.modules, 'dashboard/tiles');
            },
        },
        methods: {
            /**
             * Called when Dashboard is initially rendered, and when a Segment is saved via
             * the Segments component.
             *
             * This will do the actual call the dashboard API endpoint to retrieve metrics
             * according to Segments and the defined Filters within.
             *
             * @returns {Promise<void>}
             * @public
             */
            async drawMetrics() {
                if (!this.hasSegments) {
                    this.$stopLoading('metrics');
                    return;
                }
                this.$startLoading('metrics');
                try {
                    const settings = JSON.stringify(this.activeSegments
                        .map(segment => new Segment({...segment}).getPayload()));
                    const params = {
                        periodStart: this.period.start.format(),
                        periodEnd: this.period.end.format(),
                        segments: settings,
                        tz: this.$tz().offset(),
                    };
                    if (Array.isArray(this.tilePreferences) && this.tilePreferences.length > 0) {
                        // We clone via spread because we can't directly mutate the store
                        const orderedTiles = [...this.tilePreferences];
                        params.metrics = orderedTiles.sort((a, b) => {
                            if (a.order === b.order) {
                                return 0;
                            }

                            return a.order < b.order ? -1 : 1;
                        }).map(metric => metric.metric).join(',');
                    }

                    const {items} = await this.$api(({reports}) => reports.getDashboardMetrics(params));
                    this.metrics = items.map(metric => metric.fields);
                } catch (err) {
                    this.metrics = [];
                    DashboardBus.$emit('server-error');
                }
                this.$stopLoading('metrics');
            },
            /**
             * Determine whether a given Segment id is active on the Dashboard.
             * @param segment
             * @public
             * @returns {*}
             */
            isHiddenSegment(segment) {
                return this.$store.getters['dashboard/isHidden'](segment.id);
            },
            /**
             * Hide this tile from the dashboard reports. It will not pull it from the API any
             * longer.
             * @param index
             */
            hideTile(index) {
                this.metrics.splice(index, 1);
                this.saveTileOrder();
            },
            /**
             * Save the current metric tile order and visibility to the store.
             * @returns {Promise<void>}
             */
            saveTileOrder() {
                const data = this.metrics.map((metric, index) => ({
                    metric: metric.metric,
                    order: index,
                }));
                this.setUserPreference({type: PreferenceTypes.modules, id: 'dashboard/tiles', data});
            },
            /**
             * Reset the metric tile visibility and order to default.
             */
            resetTiles() {
                this.setUserPreference({type: PreferenceTypes.modules, id: 'dashboard/tiles', data: null});
                this.drawMetrics();
            },
            onStart() {
                this.$el.classList.add('text-unselectable');
            },
            onEnd() {
                this.$el.classList.remove('text-unselectable');
                this.$refs.tooltipElement
                    .forEach((el) => {
                        if (el.$el && el.$el.vTooltipLeave) {
                            el.$el.vTooltipLeave();
                        }
                    });
            },
        },
        created() {
            if (this.hasSegments) {
                this.$startLoading('metrics');
            }
            DashboardBus.$on('segments-updated', this.drawMetrics);
            DashboardBus.$on('period-change', this.drawMetrics);
            DashboardBus.$on('reset-tiles', this.resetTiles);
        },
        beforeDestroy() {
            DashboardBus.$off(['segments-updated', 'period-change', 'reset-tiles']);
        },
    };
</script>
